import React, { useState, useEffect } from 'react';
import {
  FaShieldAlt,
  FaSearch,
  FaEye,
  FaBan,
  FaExclamationTriangle,
  FaCheckCircle,
  FaFilter,
} from 'react-icons/fa';
import { AdminAffiliateApiService } from '../../../service/admin/adminAffiliateApiService';
import './FraudMonitor.css';

export default function FraudMonitor() {
  const [alerts, setAlerts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFraudAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSeverity, filterStatus]);

  const fetchFraudAlerts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterSeverity !== 'all') params.severity = filterSeverity;
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await AdminAffiliateApiService.getFraudAlerts(params);

      // ✅ API returns { alerts: [], pagination: {} }
      const list = response?.data?.alerts || [];
      setAlerts(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching fraud alerts:', error);
      setAlerts([]); // safety
    } finally {
      setLoading(false);
    }
  };

  const safeAlerts = Array.isArray(alerts) ? alerts : [];

  const filteredAlerts = safeAlerts.filter((alert) => {
    const q = String(searchTerm || '').toLowerCase();
    const affiliate = String(alert?.affiliate || alert?.userName || '').toLowerCase();
    const type = String(alert?.type || '').toLowerCase();
    const affiliateId = String(alert?.affiliateId || alert?.userId || '').toLowerCase();

    const matchesSearch =
      !q || affiliate.includes(q) || type.includes(q) || affiliateId.includes(q);

    const sev = String(alert?.severity || '').toLowerCase();
    const st = String(alert?.status || '').toLowerCase();

    const matchesSeverity =
      filterSeverity === 'all' || sev === String(filterSeverity).toLowerCase();

    const matchesStatus =
      filterStatus === 'all' || st === String(filterStatus).toLowerCase();

    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const getSeverityColor = (severity) => {
    const s = String(severity || '').toLowerCase();
    switch (s) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'pending review':
        return 'warning';
      case 'under investigation':
        return 'info';
      case 'resolved':
        return 'success';
      case 'blocked':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getRiskScoreColor = (score) => {
    const n = Number(score || 0);
    if (n >= 80) return 'danger';
    if (n >= 60) return 'warning';
    if (n >= 40) return 'info';
    return 'success';
  };

  const handleBlockUser = async (userId) => {
    const reason = prompt('Please enter reason for blocking this user:');
    if (!reason) return;

    try {
      await AdminAffiliateApiService.blockFraudUser(userId, reason);
      fetchFraudAlerts(); // Refresh the list
    } catch (error) {
      console.error('Error blocking user:', error);
    }
  };

  const totalAlerts = safeAlerts.length;
  const highPriorityCount = safeAlerts.filter((a) => {
    const s = String(a?.severity || '').toLowerCase();
    return s === 'critical' || s === 'high';
  }).length;

  const potentialLossPrevented = safeAlerts
    .reduce((sum, a) => sum + Number(a?.potentialLoss || 0), 0)
    .toLocaleString();

  return (
    <div className="affiliate-fraud-page">
      <div className="affiliate-fraud-header">
        <h1>Fraud Monitor</h1>
        <p>Detect and prevent fraudulent activities in the affiliate program</p>
      </div>

      <div className="admin-stats-cards">
        <div className="stat-card">
          <div className="fm-stat-icon">
            <FaExclamationTriangle />
          </div>
          <div className="fm-stat-content">
            <h3>{totalAlerts}</h3>
            <p>Total Alerts</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="fm-stat-icon">
            <FaExclamationTriangle />
          </div>
          <div className="fm-stat-content">
            <h3>{highPriorityCount}</h3>
            <p>High Priority</p>
          </div>
        </div>

        <div className="affiliate-fraud-stat-card">
          <button className="fraud-monitor-btn-secondary" type="button">
            <FaFilter /> Filter
          </button>
          <div className="fm-stat-content">
            <h3>₹{potentialLossPrevented}</h3>
            <p>Potential Loss Prevented</p>
          </div>
        </div>
      </div>

      <div className="affiliate-fraud-actions-bar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search alerts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="affiliate-fraud-filter-controls">
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="fraud-monitor-form-select"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="fraud-monitor-form-select"
          >
            <option value="all">All Status</option>
            <option value="pending review">Pending Review</option>
            <option value="under investigation">Under Investigation</option>
            <option value="resolved">Resolved</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
      </div>

      <div className="affiliate-fraud-alerts-container">
        {loading ? (
          <div className="loading-state">Loading fraud alerts...</div>
        ) : filteredAlerts.length === 0 ? (
          <div className="affiliate-fraud-empty-state">No fraud alerts found</div>
        ) : (
          filteredAlerts.map((alert) => {
            const id = alert?._id || alert?.id || `${alert?.type}-${alert?.createdAt}`;
            const status = alert?.status || 'Under Investigation';

            const affiliateLabel =
              alert?.affiliate ||
              alert?.userName ||
              alert?.userEmail ||
              '-';

            const affiliateIdLabel =
              alert?.affiliateId ||
              alert?.userId ||
              '-';

            const detectedDate =
              alert?.detectedDate ||
              (alert?.createdAt ? new Date(alert.createdAt).toLocaleString() : '-');

            const affectedOrders =
              alert?.affectedOrders != null ? alert.affectedOrders : '-';

            const evidenceList = Array.isArray(alert?.evidence) ? alert.evidence : [];
            const actionsList = Array.isArray(alert?.actionsTaken)
              ? alert.actionsTaken
              : Array.isArray(alert?.actions)
                ? alert.actions
                : [];

            return (
              <div key={id} className="fraud-alert-card">
                <div className="affiliate-fraud-alert-header">
                  <div className="alert-title">
                    <h3>{alert?.type || '-'}</h3>
                    <div className="affiliate-fraud-alert-meta">
                      <span className={`affiliate-fraud-severity-badge ${getSeverityColor(alert?.severity)}`}>
                        {alert?.severity || '-'}
                      </span>
                      <span className={`status-badge ${getStatusColor(status)}`}>
                        {status}
                      </span>
                      <span className="affiliate-fraud-risk-score">
                        Risk Score:{' '}
                        <span className={`affiliate-fraud-score ${getRiskScoreColor(alert?.riskScore)}`}>
                          {Number(alert?.riskScore || 0)}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="alert-actions">
                    <button
                      type="button"
                      className="affiliate-fraud-btn-icon affiliate-fraud-btn-view"
                      title="View"
                    >
                      <FaEye />
                    </button>

                    {String(status).toLowerCase() !== 'blocked' &&
                      String(status).toLowerCase() !== 'resolved' && (
                        <button
                          type="button"
                          className="affiliate-fraud-btn-icon affiliate-fraud-btn-block"
                          title="Block user"
                          onClick={() => handleBlockUser(alert?.userId || alert?.userId)}
                        >
                          <FaBan />
                        </button>
                      )}
                  </div>
                </div>

                <div className="affiliate-fraud-alert-content">
                  <div className="alert-description">
                    <p>
                      <strong>Description:</strong> {alert?.description || '-'}
                    </p>
                    <p>
                      <strong>Details:</strong> {alert?.details || '-'}
                    </p>
                  </div>

                  <div className="affiliate-fraud-alert-info-grid">
                    <div className="info-item">
                      <span className="affiliate-fraud-info-label">Affiliate:</span>
                      <span className="info-value">
                        {affiliateLabel} ({affiliateIdLabel})
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="affiliate-fraud-info-label">Detected:</span>
                      <span className="info-value">{detectedDate}</span>
                    </div>

                    <div className="info-item">
                      <span className="affiliate-fraud-info-label">Affected Orders:</span>
                      <span className="info-value">{affectedOrders}</span>
                    </div>

                    <div className="info-item">
                      <span className="affiliate-fraud-info-label">Potential Loss:</span>
                      <span className="affiliate-fraud-info-value affiliate-fraud-loss-amount">
                        ₹{Number(alert?.potentialLoss || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="alert-evidence">
                    <h4>Evidence:</h4>
                    {evidenceList.length ? (
                      <ul>
                        {evidenceList.map((ev, index) => (
                          <li key={index}>{ev}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="affiliate-fraud-text-muted">No evidence provided</div>
                    )}
                  </div>

                  <div className="alert-actions-taken">
                    <h4>Actions Taken:</h4>
                    <div className="affiliate-fraud-actions-list">
                      {actionsList.length ? (
                        actionsList.map((action, index) => (
                          <div key={index} className="action-item">
                            <FaCheckCircle className="affiliate-fraud-action-icon" />
                            <span>{action}</span>
                          </div>
                        ))
                      ) : (
                        <div className="affiliate-fraud-text-muted">No actions recorded</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}